import { Text } from "~/components/Text"
import { useLayoutEffect, useRef, type ReactNode } from "react"
import classNames from "classnames"
import { Flex, Input, InputNumber } from "antd"
import { ClockCircleOutlined } from "@ant-design/icons"
import { AssetPromptInput } from "~/components/AssetPromptInput"
import {
  getStoryboardShotSummary,
  type StoryboardRow,
} from "~/common/video/storyboard"
import type { UploadedResource } from "~/common/media/uploadedResources"
import { formatDurationSeconds, roundToDecimals } from "~/common/utils"
import styles from "./index.css"

type EditableStoryboardField =
  | "audio_voice"
  | "marketing_role"
  | "visual_description"

type StoryboardEditorProps = {
  variant?: "default" | "outlined"
  actions?: ReactNode
  editableFields?: EditableStoryboardField[]
  mentionableResources?: UploadedResource[]
  readonly?: boolean
  rows: StoryboardRow[]
  visualErrorShotNumbers?: number[]
  showVoiceoverLengthHint?: boolean
  showSummary?: boolean
  timeEditing?: {
    errorShotNumbers?: number[]
    max: number
    min: number
  }
  onChange?: (rows: StoryboardRow[]) => void
  onEdit?: () => void
}

const getVoiceoverLength = (value: string) => {
  return value.replace(/\p{P}/gu, "").length
}

export const StoryboardEditor = ({
  variant = "default",
  actions,
  editableFields = ["visual_description", "marketing_role", "audio_voice"],
  mentionableResources = [],
  readonly = false,
  rows,
  visualErrorShotNumbers = [],
  showVoiceoverLengthHint = false,
  showSummary = true,
  timeEditing,
  onChange,
  onEdit,
}: StoryboardEditorProps) => {
  const editableFieldSet = new Set<EditableStoryboardField>(editableFields)
  const rowsRef = useRef(rows)

  useLayoutEffect(() => {
    rowsRef.current = rows
  }, [rows])

  const updateStoryboardRow = (
    shotNumber: number,
    field: EditableStoryboardField,
    value: string,
  ) => {
    if (!onChange) return

    const nextRows = rowsRef.current.map((row) => {
      if (row.shot_number !== shotNumber) return row

      return {
        ...row,
        [field]: value,
      }
    })

    rowsRef.current = nextRows
    onChange(nextRows)
  }

  const updateStoryboardTime = (
    row: StoryboardRow,
    field: "end_time" | "start_time",
    value: number,
  ) => {
    if (!onChange) return

    const nextRows = rowsRef.current.map((currentRow) => {
      if (currentRow.shot_number !== row.shot_number) return currentRow

      const nextRow = {
        ...currentRow,
        [field]: roundToDecimals(value),
      }

      return {
        ...nextRow,
        duration: Math.max(
          0,
          roundToDecimals(nextRow.end_time - nextRow.start_time),
        ),
      }
    })

    rowsRef.current = nextRows
    onChange(nextRows)
  }

  return (
    <Flex
      orientation="vertical"
      gap={12}
    >
      {rows.map((row, index) => {
        const recommendedVoiceoverLength = Math.ceil(row.duration * 6)
        const voiceoverLength = getVoiceoverLength(row.audio_voice)
        const isVoiceoverTooLong = voiceoverLength > recommendedVoiceoverLength
        const hasTimeError = timeEditing?.errorShotNumbers?.includes(
          row.shot_number,
        )
        const isLastShot = index === rows.length - 1
        const hasVisualError = visualErrorShotNumbers.includes(row.shot_number)

        return (
          <div
            key={row.shot_number}
            className={classNames(
              styles.storyboardCard,
              variant === "outlined" && styles.outlined,
              readonly && styles.readonly,
            )}
          >
            <Flex
              className={styles.storyboardHeader}
              align="center"
              justify="space-between"
              wrap
            >
              <Text
                size="m"
                className={styles.storyboardSummary}
              >
                {variant === "outlined" ? (
                  <Text
                    size="m"
                    color="var(--ant-color-bg-container)"
                    className={styles.shotNumber}
                  >
                    {row.shot_number}
                  </Text>
                ) : null}
                <Text strong>分镜&nbsp;{row.shot_number}&nbsp;&nbsp;</Text>
                {showSummary ? (
                  <Text
                    size="s"
                    type="secondary"
                    style={{ lineHeight: "18px" }}
                  >
                    · {getStoryboardShotSummary(row)}
                  </Text>
                ) : null}
              </Text>
              <Flex
                align="center"
                gap={12}
                wrap
              >
                {timeEditing && !readonly ? (
                  <Flex
                    align="center"
                    className={styles.storyboardTimeEditor}
                    gap={8}
                    wrap
                  >
                    <ClockCircleOutlined />
                    <InputNumber
                      className={styles.timeInput}
                      min={0}
                      precision={1}
                      status={hasTimeError ? "error" : undefined}
                      step={0.1}
                      value={row.start_time}
                      onFocus={onEdit}
                      onChange={(value) => {
                        if (typeof value !== "number") return
                        updateStoryboardTime(row, "start_time", value)
                      }}
                    />
                    <Text type="secondary">-</Text>
                    <InputNumber
                      className={styles.timeInput}
                      max={timeEditing.max}
                      min={isLastShot ? timeEditing.min : undefined}
                      precision={1}
                      status={hasTimeError ? "error" : undefined}
                      step={0.1}
                      value={row.end_time}
                      onFocus={onEdit}
                      onChange={(value) => {
                        if (typeof value !== "number") return
                        updateStoryboardTime(row, "end_time", value)
                      }}
                    />
                    <Text type="secondary">秒</Text>
                  </Flex>
                ) : (
                  <Text
                    size="s"
                    className={styles.storyboardTime}
                    type="secondary"
                  >
                    <ClockCircleOutlined />
                    {formatDurationSeconds(row.start_time)}-
                    {formatDurationSeconds(row.end_time)}
                  </Text>
                )}
                {actions}
              </Flex>
            </Flex>

            <Flex
              className={styles.storyboardFields}
              orientation="vertical"
              gap={12}
            >
              {editableFieldSet.has("visual_description") ? (
                <Flex
                  vertical
                  gap={8}
                  onFocus={(event) => {
                    if (
                      !readonly &&
                      !event.currentTarget.contains(event.relatedTarget)
                    ) {
                      onEdit?.()
                    }
                  }}
                >
                  <Text
                    size="s"
                    className={styles.storyboardLabel}
                    type="secondary"
                  >
                    画面动作描述
                  </Text>
                  {readonly ? (
                    <AssetPromptInput
                      readOnly
                      className={styles.readonlyField}
                      mentionableResources={mentionableResources}
                      placeholder=""
                      value={row.visual_description || "-"}
                    />
                  ) : (
                    <AssetPromptInput
                      className={classNames(
                        styles.fieldShell,
                        hasVisualError && styles.fieldError,
                      )}
                      mentionableResources={mentionableResources}
                      placeholder="输入镜头生成提示，支持 @ 当前任务里的图片或视频资源。"
                      onTextChange={(value) => {
                        updateStoryboardRow(
                          row.shot_number,
                          "visual_description",
                          value,
                        )
                      }}
                      value={row.visual_description}
                    />
                  )}
                  {!readonly && hasVisualError ? (
                    <Text
                      type="danger"
                      role="alert"
                    >
                      画面动作描述不能为空
                    </Text>
                  ) : null}
                </Flex>
              ) : null}
              {editableFieldSet.has("marketing_role") ? (
                <Flex
                  vertical
                  gap={8}
                >
                  <Text
                    size="s"
                    className={styles.storyboardLabel}
                    type="secondary"
                  >
                    该镜头的营销目的
                  </Text>
                  {readonly ? (
                    <AssetPromptInput
                      readOnly
                      className={styles.readonlyField}
                      mentionableResources={mentionableResources}
                      placeholder=""
                      value={row.marketing_role || "-"}
                    />
                  ) : (
                    <Input.TextArea
                      variant="filled"
                      className={styles.storyboardTextArea}
                      autoSize={{ minRows: 2 }}
                      value={row.marketing_role}
                      onChange={(event) =>
                        updateStoryboardRow(
                          row.shot_number,
                          "marketing_role",
                          event.target.value,
                        )
                      }
                    />
                  )}
                </Flex>
              ) : null}

              {editableFieldSet.has("audio_voice") ? (
                <Flex
                  gap={8}
                  vertical
                  style={{ position: "relative" }}
                >
                  <Text
                    size="s"
                    className={styles.storyboardLabel}
                    type="secondary"
                  >
                    {variant === "outlined" ? (
                      <>
                        口播内容
                        <span className={styles.voiceoverDescription}>
                          （仅修改口播话术，前面“声音1=XX”不要改动，否则容易出错。建议一秒4~6个字）
                        </span>
                      </>
                    ) : (
                      '口播内容（只修改口播话术，前面"声音1=XX"不要改动，否则容易出错。建议一秒4~6个字）'
                    )}
                  </Text>
                  {readonly ? (
                    <AssetPromptInput
                      readOnly
                      className={classNames(
                        styles.readonlyField,
                        styles.readonlyVoice,
                      )}
                      mentionableResources={mentionableResources}
                      placeholder=""
                      value={
                        row.audio_voice ||
                        (variant === "outlined" ? "（无口播，点击编辑）" : "-")
                      }
                    />
                  ) : (
                    <Input.TextArea
                      variant={variant === "outlined" ? "outlined" : "filled"}
                      className={styles.storyboardTextArea}
                      autoSize={{ minRows: 2 }}
                      placeholder="若不填，则视为该分镜无口播内容"
                      value={row.audio_voice}
                      onFocus={onEdit}
                      onChange={(event) =>
                        updateStoryboardRow(
                          row.shot_number,
                          "audio_voice",
                          event.target.value,
                        )
                      }
                    />
                  )}
                  {showVoiceoverLengthHint ? (
                    <Text
                      size="s"
                      className={styles.voiceoverHint}
                      type={isVoiceoverTooLong ? "warning" : "secondary"}
                    >
                      当前 {voiceoverLength} 字 / 建议 ≤{" "}
                      {recommendedVoiceoverLength} 字
                      {isVoiceoverTooLong ? "，口播可能过快，建议精简文案" : ""}
                    </Text>
                  ) : null}
                </Flex>
              ) : null}
            </Flex>
          </div>
        )
      })}
    </Flex>
  )
}
